import { ArrowRightLeft, Building2, Loader2, User, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useFetchUserByTenant } from '@/features/users/services'
import { useFetchPermissionsByService } from '@/features/permissions/services/useFetchPermissionsByService'
import { TypeCargo } from '@/lib/enums'
import { useState, useMemo, useEffect } from 'react'
import { TypeViewProduct } from '../../state/selectProduct'
import { toast } from '@/lib/hooks/use-toast'
import { useAssignAnalystProduct } from '../../services/useAssignAnalystProduct'
import { formatNome } from '@/utils/format'
import { useUpdateResponsavelSingle } from '@/features/client/services/useUpdateResponsavelSingle'
import { useQueryClient } from '@tanstack/react-query'

type Props = {
  clientInfo: {
    parceiro: string
    empresa: string
    nomeAnalista: string
    id_client?: string
    id_parceiro?: string | null
    id_analista?: string | null
  }
  type: TypeViewProduct
  productId: string | undefined
}

type SlimUser = { id: string; id_user: string; name: string; typeCargo?: TypeCargo }

export function InfoRelacao({ clientInfo, type, productId }: Props) {
  const user = useCurrentUser()
  const { data: usersAnalista } = useFetchPermissionsByService(type, TypeCargo.ANALISTA)

  const { data: usersTenant } = useFetchUserByTenant()
  const queryClient = useQueryClient()
  // estado dos diálogos e selections
  const [openPartner, setOpenPartner] = useState(false)
  const [openAnalyst, setOpenAnalyst] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<string | undefined>(undefined)
  const [selectedAnalyst, setSelectedAnalyst] = useState<string | undefined>(undefined)

  const { mutate: transferPartner, isPending: isTransferringPartner } = useUpdateResponsavelSingle()

  const { mutate: transferAnalyst, isPending: isAssigningAnalyst } = useAssignAnalystProduct()

  // listas derivadas
  const partnerOptions: SlimUser[] = useMemo(() => {
    const list = (usersTenant ?? []) as SlimUser[]
    const filtered = list.filter(u =>
      [TypeCargo.PARCEIRO, TypeCargo.CEO, TypeCargo.GERENTE].includes(u.typeCargo as TypeCargo)
    )
    return filtered
  }, [usersTenant])

  const analystOptions: SlimUser[] = useMemo(() => {
    const list = (usersAnalista ?? []) as SlimUser[]

    return list
  }, [usersAnalista])

  const canTransferPartner =
    user?.typeUser === 'USUARIO' && (user?.typeCargo === 'CEO' || user?.typeCargo === 'GERENTE')

  const canTransferAnalyst =
    user?.typeUser === 'ADMIN' && user?.typeCargo === 'ANALISTA' && user?.permissions?.update_user_product

  function handleSavePartner() {
    if (!clientInfo.id_client || !selectedPartner) return
    if (clientInfo.id_parceiro && selectedPartner === clientInfo.id_parceiro) {
      toast({ title: 'Nada a mudar', description: 'O responsável selecionado já é o atual.' })
      return
    }

    transferPartner(
      { id_client: String(clientInfo.id_client), id_responsavel: String(selectedPartner) },
      {
        onSuccess: (res: any) => {
          toast({
            title: 'Responsável atualizado',
            description:
              res?.data?.affected > 0 ? 'Transferência concluída com sucesso.' : 'Nenhum registro foi alterado.'
          })
          // 🔁 invalida produto(s) após sucesso
          queryClient.invalidateQueries({ queryKey: ['product', clientInfo.id_client] })
          queryClient.invalidateQueries({ queryKey: ['product'] }) // se houver lista
        },
        onError: (err: any) => {
          toast({
            variant: 'destructive',
            title: 'Falha ao atualizar responsável',
            description: err?.message || 'Tente novamente mais tarde.'
          })
        },
        onSettled: () => setOpenPartner(false)
      }
    )
  }

  // 🔵 estado otimista do analista
  const [optimisticAnalystName, setOptimisticAnalystName] = useState<string | null>(null)
  const [optimisticPending, setOptimisticPending] = useState(false)

  // nome efetivo exibido (prioriza o otimista se existir)
  const effectiveAnalystName = optimisticAnalystName ?? (clientInfo?.nomeAnalista || '—')
  const formattedCompanyName = clientInfo.empresa ? formatNome(clientInfo.empresa) : '—'
  const formattedPartnerName = clientInfo.parceiro ? formatNome(clientInfo.parceiro) : '—'
  const formattedAnalystName =
    effectiveAnalystName && effectiveAnalystName !== '—' && effectiveAnalystName !== 'Sem analista'
      ? formatNome(effectiveAnalystName)
      : effectiveAnalystName

  // mantém o nome otimista até o server refletir o mesmo valor
  useEffect(() => {
    if (optimisticAnalystName && clientInfo?.nomeAnalista === optimisticAnalystName) {
      setOptimisticAnalystName(null) // server confirmou -> limpa estado otimista
    }
  }, [clientInfo?.nomeAnalista, optimisticAnalystName])

  // util: descobrir o nome pelo id selecionado
  const resolveAnalystName = (id: string | undefined) => {
    if (!id) return null
    if (id === '__NONE__') return 'Sem analista'
    const found = analystOptions.find(u => String(u.id_user) === String(id))
    return found ? formatNome(found.name ?? '') : null
  }

  function handleSaveAnalyst() {
    if (!productId || !selectedAnalyst) return

    // snapshot para rollback em erro
    const prevName = clientInfo?.nomeAnalista || '—'
    const nextName = resolveAnalystName(selectedAnalyst) ?? prevName
    const payloadIdAnalista = selectedAnalyst === '__NONE__' ? null : String(selectedAnalyst)

    // 🔵 aplica otimista
    setOptimisticAnalystName(nextName)
    setOptimisticPending(true)

    transferAnalyst(
      { id_product: String(productId), id_analista: payloadIdAnalista as any },
      {
        onSuccess: res => {
          // opcional: se a API já devolve o nome padronizado, usa ele
          const serverName = res?.data?.nomeAnalista ?? nextName
          setOptimisticAnalystName(serverName)
          toast({
            title: 'Analista atualizado',
            description: res?.data?.nomeAnalista
              ? `Produto atribuído para ${serverName}.`
              : 'Atribuição removida (sem analista).'
          })

          // ✅ refresca cache relevante
          // ajuste as keys ao seu app (ex.: ['product', productId], ['client', cnpj] etc.)
          queryClient.invalidateQueries({ queryKey: ['product', productId] })
          queryClient.invalidateQueries({ queryKey: ['product'] })
        },
        onError: (err: any) => {
          // ⛔ rollback
          setOptimisticAnalystName(prevName)
          toast({
            variant: 'destructive',
            title: 'Falha ao atualizar',
            description: err?.message || 'Tente novamente mais tarde.'
          })
        },
        onSettled: () => {
          setOptimisticPending(false)
          setOpenAnalyst(false)
        }
      }
    )
  }

  const relationCardClass =
    'min-w-0 rounded-lg border p-3 shadow-sm transition-all duration-300 hover:shadow-md'

  return (
    <div className="grid w-full grid-cols-1 gap-3">
      {/* Empresa */}
      <div className={`${relationCardClass} border-gray-100/60 bg-gradient-to-br from-white to-gray-50/30 hover:border-gray-200`}>
        <div className="flex items-start gap-2">
          <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" />
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-600">Empresa</p>
            <p className="break-words text-sm font-semibold leading-tight text-gray-800" title={formattedCompanyName}>
              {formattedCompanyName}
            </p>
          </div>
        </div>
      </div>

      {/* Parceiro (Responsável) */}
      <div
        className={`${relationCardClass} border-blue-100/60 bg-gradient-to-br from-white to-blue-50/30 hover:border-blue-200`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <User className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-blue-600">Responsável</p>
              <p className="break-words text-sm font-semibold leading-tight text-gray-800" title={formattedPartnerName}>
                {formattedPartnerName}
              </p>
            </div>
          </div>

          {canTransferPartner && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 px-2 text-xs hover:bg-blue-100 hover:text-blue-700"
              title="Transferir parceiro"
              onClick={() => setOpenPartner(true)}
            >
              <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />
              Transferir
            </Button>
          )}
        </div>
      </div>

      {/* Analista */}
      <div
        className={`${relationCardClass} border-green-100/60 bg-gradient-to-br from-white to-green-50/30 hover:border-green-200`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-green-600">Analista</p>
              <p
                className="flex items-start gap-1 break-words text-sm font-semibold leading-tight text-gray-800"
                title={formattedAnalystName}
                aria-live="polite"
              >
                <span className="min-w-0 break-words">{formattedAnalystName}</span>
                {optimisticPending && <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" aria-label="Salvando..." />}
              </p>
            </div>
          </div>

          {canTransferAnalyst && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 px-2 text-xs hover:bg-green-100 hover:text-green-700"
              title="Transferir analista"
              onClick={() => setOpenAnalyst(true)}
            >
              <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />
              Transferir
            </Button>
          )}
        </div>
      </div>

      {/* Dialog: Transferir Parceiro */}
      <Dialog open={openPartner} onOpenChange={setOpenPartner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transferir responsável</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Selecione o novo responsável</label>
            <Select value={selectedPartner} onValueChange={v => setSelectedPartner(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Escolher usuário" />
              </SelectTrigger>
              <SelectContent>
                {partnerOptions.map(u => {
                  const id = String(u.id)
                  return (
                    <SelectItem key={id} value={id}>
                      {formatNome(u.name ?? '')}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenPartner(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePartner} disabled={!selectedPartner || isTransferringPartner}>
              {isTransferringPartner ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Transferir Analista */}
      <Dialog open={openAnalyst} onOpenChange={setOpenAnalyst}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transferir analista</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              Selecione o novo analista para o serviço <span className="font-medium">{type}</span>
            </label>
            <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
              <SelectTrigger>
                <SelectValue placeholder="Escolher analista" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key="__NONE__" value="__NONE__">
                  Sem analista
                </SelectItem>
                {analystOptions.map(u => {
                  const id = String(u.id_user)
                  return (
                    <SelectItem key={id} value={id}>
                      {formatNome(u.name ?? '')}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenAnalyst(false)} disabled={optimisticPending}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAnalyst} disabled={!selectedAnalyst || isAssigningAnalyst}>
              {isAssigningAnalyst || optimisticPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
